import { Head, router, useForm, usePage } from "@inertiajs/react";
import { FileText, Upload } from "lucide-react";
import { useState } from "react";
import type React from "react";
import Dropzone from "shadcn-dropzone";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FilePreview } from "@/components/ui/file-preview";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import { useCountdown } from "@/hooks/use-countdown";
import { quoteInvitationPath } from "@/routes";
import { formatDateTimeAR } from "@/utils/date";

interface SubmittedDoc {
  filename: string;
  created_at: string;
}

interface PageProps {
  supplier_name: string;
  purchase_request_number: string;
  purchase_request_pdf_url: string;
  token: string;
  previously_submitted_docs: SubmittedDoc[];
  errors: Record<string, string>;
  deadline_at?: string | null;
  is_expired?: boolean;
}



export default function QuoteSubmissionPage({
  supplier_name,
  purchase_request_number,
  purchase_request_pdf_url,
  token,
  previously_submitted_docs = [],
  errors,
  deadline_at,
  is_expired = false,
}: PageProps) {
  const page = usePage<{ branding?: { company_name?: string; logo_url?: string | null; main_color?: string } }>();
  const branding = page.props.branding ?? {};
  const mainColor = branding.main_color ?? "#111827";
  const initials = (branding.company_name ?? "")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const { data, setData, reset } = useForm({
    pdf: null as File | null,
  });
  const [submitting, setSubmitting] = useState(false);
  const countdown = useCountdown({
    deadline: deadline_at ?? undefined,
    isExpired: is_expired
  });

   
  const isDisabled = submitting || is_expired;

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (!data.pdf) return;
    router.post(
      quoteInvitationPath(token),
      { pdf: data.pdf },
      {
        forceFormData: true,
        onStart: () => setSubmitting(true),
        onFinish: () => setSubmitting(false),
        onSuccess: () => reset("pdf"),
      },
    );
  };

  const handleFileChange = (acceptedFiles: File[]) => {
    if (is_expired) return;
    const file = acceptedFiles[0] ?? null;
    setData("pdf", file);
    if (file) {
      router.post(
        quoteInvitationPath(token),
        { pdf: file },
        {
          forceFormData: true,
          onStart: () => setSubmitting(true),
          onFinish: () => setSubmitting(false),
          onSuccess: () => reset("pdf"),
        },
      );
    }
  };

  const handleFileRemove = () => {
    setData("pdf", null);
  };

  return (
    <>
      <Head title={`Enviar presupuesto · ${supplier_name}`} />
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-xl md:max-w-2xl">
          {/* Branded strip above the card */}
          <div
            className="mb-4 rounded-xl border shadow-sm"
            style={{ backgroundColor: mainColor }}
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <Avatar className="size-12 md:size-14 ring-1 ring-white/70 bg-white">
                <AvatarImage src={branding.logo_url ?? undefined} alt={branding.company_name ?? "Logo"} />
                <AvatarFallback className="bg-white text-slate-800 font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="text-white">
                <div className="text-sm md:text-base font-semibold leading-tight">
                  {branding.company_name ?? ""}
                </div>
              </div>
            </div>
          </div>

          <Card className="shadow-sm overflow-hidden">
            <CardHeader className="border-b bg-gradient-to-br from-muted/40 to-transparent">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-xl leading-tight truncate" title={supplier_name}>
                    {supplier_name}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="px-1.5 py-0.5 text-[11px]">
                      Solicitud #{purchase_request_number}
                    </Badge>
                    {is_expired ? (
                      <Badge variant="destructive" className="px-1.5 py-0.5 text-[11px]">Expirada</Badge>
                    ) : (
                      <Badge variant="secondary" className="px-1.5 py-0.5 text-[11px]">Documentos (PDF o imagen)</Badge>
                    )}
                  </CardDescription>
                </div>
                {!is_expired && deadline_at && countdown && (
                  <div className="text-xs text-muted-foreground shrink-0">
                    {countdown}
                  </div>
                )}
              </div>
            </CardHeader>

            <form onSubmit={handleSubmit} className="contents">
              <CardContent className="flex flex-col gap-6">
                {is_expired && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
                    {`Estos requerimientos ya expiraron. Esta solicitud cerró${deadline_at ? ` el ${formatDateTimeAR(deadline_at)}` : ""}.`}
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm">Archivo del presupuesto</Label>
                      {!is_expired && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                          Documentos (PDF o imagen)
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {purchase_request_pdf_url && (
                        <Button asChild variant="outline" size="sm" className="h-8">
                          <a
                            href={purchase_request_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Ver solicitud original
                          </a>
                        </Button>
                      )}
                      <span className="text-xs text-muted-foreground">
                        Revisala antes de enviar tu archivo. Si luego necesitás actualizar tu presupuesto, podés volver a subir otro archivo por acá.
                      </span>
                    </div>
                  </div>

                  {!data.pdf ? (
                    <Dropzone
                      onDrop={handleFileChange}
                      accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                      maxFiles={1}
                      disabled={isDisabled}
                    >
                      {() => (
                        <div className="flex justify-center items-center w-full h-32 rounded-xl bg-muted/30 hover:bg-accent hover:text-accent-foreground transition-colors select-none cursor-pointer">
                          <div className="flex items-center flex-col gap-1.5 px-4 text-center">
                            <div className="inline-flex items-center gap-2 text-sm font-medium">
                              <Upload className="h-4 w-4 opacity-80" />
                              {is_expired ? "Subida deshabilitada" : "Tocá o arrastrá tu archivo"}
                            </div>
                            {!is_expired && (
                              <p className="text-xs text-muted-foreground">
                                El envío se realiza automáticamente al seleccionar
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </Dropzone>
                  ) : (
                    <FilePreview
                      file={data.pdf}
                      onRemove={handleFileRemove}
                      disabled={submitting}
                    />
                  )}
                  {errors?.pdf && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.pdf}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  {!is_expired && (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">Cómo funciona</p>
                        <Badge variant="outline" className="text-[10px]">Autoenvío</Badge>
                      </div>
                      <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-1">
                        <li>Descargá y revisá la solicitud.</li>
                        <li>Subí tu presupuesto como documento (PDF o imagen, un único archivo).</li>
                        <li>El envío se realiza automáticamente.</li>
                        <li>¿Necesitás corregir o actualizar? Volvé a subir otro archivo: siempre usamos la última versión enviada.</li>
                      </ol>
                    </>
                  )}
                </div>

                {previously_submitted_docs.length > 0 && (
                  <div className="pt-2">
                    <Separator className="my-4" />
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-foreground">Historial de envíos</h3>
                      <span className="text-[11px] text-muted-foreground">Siempre usamos la última versión</span>
                    </div>
                    <ul className="mt-3 divide-y rounded-lg border bg-muted/20">
                      {previously_submitted_docs.map((doc, index) => (
                        <li
                          key={index}
                          className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="text-muted-foreground w-4 h-4 shrink-0" />
                            <span
                              className="font-medium text-foreground truncate"
                              title={doc.filename}
                            >
                              {doc.filename}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {doc.created_at}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>

              {/* Sin botón: envío automático al seleccionar el archivo */}
            </form>
            { }
            {Boolean((branding.company_name ?? "").trim()) || Boolean(branding.logo_url) ? (
              <div className="px-6 py-3 text-center text-xs text-muted-foreground border-t bg-muted/20">
                {branding.company_name ? `Envío para ${branding.company_name}` : ""}
              </div>
            ) : null}
          </Card>
        </div>
      </div>
    </>
  );
}
